import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { IconButton, Stack, Tooltip } from "@mui/material";
import { useState } from "react";
import { I18N_NAMESPACES } from "@src/appV2/i18n/constants";
import { useTu } from "@src/appV2/i18n/useTu";
import { Button } from "@src/appV2/redesign/components/Button";
import { Pill } from "@src/appV2/redesign/components/Pill";
import { Text } from "@src/appV2/redesign/components/Text";

interface AccountSwitcherOptionProps {
  title: string;
  subtitles?: string[];
  stage?: string;
  isSelected?: boolean;
  onClick?: () => void;
}

export function AccountSwitcherOption(props: AccountSwitcherOptionProps) {
  const { title, subtitles, stage, isSelected, onClick } = props;
  const [isCopied, setIsCopied] = useState(false);
  const { tu } = useTu(I18N_NAMESPACES.navigation);

  function handleCopy(event: React.MouseEvent) {
    event.stopPropagation();
    const allDetails = [title, ...(subtitles ?? [])].join("\n");
    void navigator.clipboard.writeText(allDetails);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  }

  return (
    <Button
      fullWidth
      sx={(theme) => ({
        height: "auto",
        flexDirection: "column",
        alignItems: "start",
        borderRadius: 0,
        border: "none",
        backgroundColor: theme.palette.navbar?.background,
        "&:hover": {
          border: "none",
          backgroundColor: theme.palette.background?.medium,
        },
        ...(isSelected && {
          backgroundColor: theme.palette.background?.muted,
          borderLeft: `4px solid ${theme.palette.base?.primary}`,
        }),
      })}
      variant="outlined"
      onClick={onClick}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
        <Stack direction="row" alignItems="center" gap={2} minWidth={0} flex={1}>
          {isSelected && (
            <CheckIcon
              sx={(theme) => ({
                fontSize: "1rem",
                color: theme.palette.base?.primary,
                flexShrink: 0,
              })}
            />
          )}
          <Text
            variant="body2"
            sx={(theme) => ({
              color: theme.palette.navbar?.itemContentSelected,
              fontWeight: isSelected ? 600 : 500,
              textAlign: "left",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              wordBreak: "break-word",
            })}
          >
            {title}
          </Text>
        </Stack>
        {stage && (
          <Pill
            size="small"
            sx={(theme) => ({
              fontSize: theme.typography.caption.fontSize,
              flexShrink: 0,
              marginLeft: 2,
            })}
          >
            {stage}
          </Pill>
        )}
      </Stack>
      {subtitles?.map((subtitle, index) => {
        const isLastSubtitle = index === (subtitles?.length ?? 0) - 1;
        return (
          <Stack
            // eslint-disable-next-line react/no-array-index-key
            key={`${index}-${subtitle}`}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Text
              variant="caption"
              noWrap
              sx={(theme) => ({
                color: theme.palette.navbar?.itemContent,
                textAlign: "left",
              })}
            >
              {String(subtitle)}
            </Text>
            {isLastSubtitle && (
              <Tooltip title={isCopied ? tu("Copied!") : tu("Copy details")}>
                <IconButton
                  size="small"
                  onClick={handleCopy}
                  sx={(theme) => ({
                    padding: 2,
                    color: theme.palette.navbar?.itemContent,
                    "&:hover": {
                      color: theme.palette.navbar?.itemContentSelected,
                    },
                  })}
                >
                  <ContentCopyIcon sx={{ fontSize: "0.875rem" }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      })}
    </Button>
  );
}
